import * as React from 'react'
import styled from '@emotion/styled'
import {css, keyframes} from '@emotion/core'
import {useTheme} from 'emotion-theming'

import {ThemeType} from 'types/Theme'
import {AppearanceType} from 'types/Button'
import {TshirtSizesType} from 'types/Sizes'
import {forwardRef, HTMLAttributes} from 'react'

type AlignTextType = 'left' | 'center' | 'right'

interface PropsType {
  /** ID for end to end testing and Fullstory monitoring */
  dataId?: string
  /** Position of the button content */
  alignText?: AlignTextType
  /** Button style */
  appearance?: AppearanceType
  /** Button content */
  children?: React.ReactNode
  /** Pass through classname to allow styles overrides */
  className?: string
  /** Whether the button is  disabled */
  disabled?: boolean
  /** Whether the button full width of parent */
  fullWidth?: boolean
  /** Whether the button is in the loading state */
  loading?: boolean
  /** The button click handler */
  onClick?: (e: React.SyntheticEvent) => void
  /** The button size */
  size?: TshirtSizesType
  /** The button tab index */
  tabIndex?: number
  /** The HTML button type */
  type?: 'submit' | 'reset' | 'button' | undefined
}

interface MakeStyledButtonPropsType {
  activeFontColor: string
  backgroundColor: string
  borderColor?: string
  borderWidth?: string
  fontColor: string
  hoverBackgroundColor: string
  activeBackgroundColor?: string
  hoverFontColor: string
  showHoverUnderline?: boolean
  activeShadow?: string
}

interface StyleProps {
  alignText?: AlignTextType
  appearance?: AppearanceType
  disabled?: boolean
  disabledStyle?: boolean
  fullWidth?: boolean
  size?: TshirtSizesType
  theme?: ThemeType
  color?: string
  delay?: string
}

const getJustifyContent = alignText => {
  const mapping = {
    left: 'flex-start',
    right: 'flex-end',
    center: 'center'
  }
  return mapping[alignText]
}

export const base = ({theme, disabled, alignText}: StyleProps) => {
  return css`
    display: inline-flex;
    border: 0;
    border-radius: ${theme.layout.radius.md}px;
    margin: 0;
    padding: 0;
    ${disabled ? 'cursor: not-allowed;' : 'cursor: pointer;'}
    align-items: center;
    justify-content: ${getJustifyContent(alignText)};
    text-align: ${alignText};
    vertical-align: middle;
    user-select: none;
    outline: none;
    position: relative;
    text-decoration: none;
    background-color: ${theme.colors.transparent};
    color: inherit;
    -webkit-appearance: none;
    -webkit-tap-highlight-color: transparent;
  `
}

export const makeStyledButton = ({
  fontColor,
  hoverFontColor,
  activeFontColor,
  backgroundColor,
  hoverBackgroundColor,
  activeBackgroundColor,
  borderColor,
  borderWidth,
  activeShadow,
  showHoverUnderline
}: MakeStyledButtonPropsType) => css`
  color: ${fontColor};
  background-color: ${backgroundColor};
  ${borderWidth &&
  borderColor &&
  `box-shadow: inset 0 0 0 ${borderWidth} ${borderColor};`}
  &:hover,
  &:focus {
    background-color: ${hoverBackgroundColor};
    ${showHoverUnderline && 'text-decoration: underline;'}
  }
  &:active {
    background-color: ${activeBackgroundColor};
    ${showHoverUnderline && 'text-decoration: underline;'}
  }
  &:hover,
  &:focus {
    color: ${hoverFontColor};
  }
  &:active {
    color: ${activeFontColor};
    ${activeShadow &&
    (borderWidth && borderColor
      ? `box-shadow: inset 0 0 0 ${borderWidth} ${borderColor}, ${activeShadow};`
      : `box-shadow: ${activeShadow};`)}
  }
`

const ButtonLoadingWrapper = styled.div`
  text-align: center;
  width: 100%;
`

const fadeInOut = keyframes`
  from {
    opacity: 0.3
  }

  to{
    opacity: 0.8
  }
`

const LoadingBullet = styled.div<StyleProps>(
  ({theme, color, delay}) => css`
    display: inline-block;
    opacity: 0;
    border-radius: 50%;
    background: ${color};
    height: 12px;
    width: 12px;
    animation: 0.6s infinite alternate ${fadeInOut};
    animation-delay: ${delay};
    &:not(:last-child) {
      margin-right: ${theme.layout.spacing.s300}px;
    }
  `
)

const btnColorPrimary = ({theme}: StyleProps) => {
  const {
    buttonColorPrimary,
    buttonHoverColorPrimary,
    buttonActiveColorPrimary,
    buttonBgPrimary,
    buttonHoverBgPrimary,
    buttonActiveBgPrimary
  } = theme.colors
  return makeStyledButton({
    fontColor: buttonColorPrimary,
    hoverFontColor: buttonHoverColorPrimary,
    activeFontColor: buttonActiveColorPrimary,
    backgroundColor: buttonBgPrimary,
    hoverBackgroundColor: buttonHoverBgPrimary,
    activeBackgroundColor: buttonActiveBgPrimary
  })
}

const btnColorDisabled = ({theme}: StyleProps) => {
  const {
    buttonColorDisabled,
    buttonHoverColorDisabled,
    buttonActiveColorDisabled,
    buttonBgDisabled,
    buttonHoverBgDisabled
  } = theme.colors

  return makeStyledButton({
    fontColor: buttonColorDisabled,
    hoverFontColor: buttonHoverColorDisabled,
    activeFontColor: buttonActiveColorDisabled,
    backgroundColor: buttonBgDisabled,
    hoverBackgroundColor: buttonHoverBgDisabled
  })
}

const btnColorSecondary = ({theme}: StyleProps) => {
  const {
    buttonColorSecondary,
    buttonHoverColorSecondary,
    buttonActiveColorSecondary,
    buttonBgSecondary,
    buttonHoverBgSecondary,
    buttonBorderSecondary,
    buttonActiveBgSecondary
  } = theme.colors

  return makeStyledButton({
    fontColor: buttonColorSecondary,
    hoverFontColor: buttonHoverColorSecondary,
    activeFontColor: buttonActiveColorSecondary,
    backgroundColor: buttonBgSecondary,
    hoverBackgroundColor: buttonHoverBgSecondary,
    activeBackgroundColor: buttonActiveBgSecondary,
    borderColor: buttonBorderSecondary,
    borderWidth: '1px'
  })
}

const btnColorQuiet = ({theme}: StyleProps) => {
  const {
    buttonColorQuiet,
    buttonHoverColorQuiet,
    buttonActiveColorQuiet,
    buttonBgQuiet,
    buttonHoverBgQuiet
  } = theme.colors

  return makeStyledButton({
    fontColor: buttonColorQuiet,
    hoverFontColor: buttonHoverColorQuiet,
    activeFontColor: buttonActiveColorQuiet,
    backgroundColor: buttonBgQuiet,
    hoverBackgroundColor: buttonHoverBgQuiet,
    showHoverUnderline: true
  })
}

const btnColorLink = ({theme}: StyleProps) => {
  const {
    buttonColorQuiet,
    buttonHoverColorQuiet,
    buttonActiveColorQuiet,
    buttonBgQuiet,
    buttonHoverBgQuiet
  } = theme.colors

  return makeStyledButton({
    fontColor: buttonColorQuiet,
    hoverFontColor: buttonHoverColorQuiet,
    activeFontColor: buttonActiveColorQuiet,
    backgroundColor: buttonBgQuiet,
    hoverBackgroundColor: buttonHoverBgQuiet,
    showHoverUnderline: true
  })
}

const btnColorPrivateDeal = ({theme}: StyleProps) => {
  const {
    buttonColorPrivateDeal,
    buttonHoverColorPrivateDeal,
    buttonActiveColorPrivateDeal,
    buttonHoverBgPrivateDeal,
    buttonActiveBgPrivateDeal,
    buttonBgPrivateDeal
  } = theme.colors

  return makeStyledButton({
    fontColor: buttonColorPrivateDeal,
    hoverFontColor: buttonHoverColorPrivateDeal,
    activeFontColor: buttonActiveColorPrivateDeal,
    backgroundColor: buttonBgPrivateDeal,
    hoverBackgroundColor: buttonHoverBgPrivateDeal,
    activeBackgroundColor: buttonActiveBgPrivateDeal
  })
}

const btnColorPrivateDealSecondary = ({theme}: StyleProps) => {
  const {
    buttonColorPrivateDealSecondary,
    buttonHoverColorPrivateDealSecondary,
    buttonActiveColorPrivateDealSecondary,
    buttonHoverBgPrivateDealSecondary,
    buttonActiveBgPrivateDealSecondary,
    buttonBgPrivateDealSecondary,
    buttonBorderPrivateDealSecondary
  } = theme.colors

  return makeStyledButton({
    fontColor: buttonColorPrivateDealSecondary,
    hoverFontColor: buttonHoverColorPrivateDealSecondary,
    activeFontColor: buttonActiveColorPrivateDealSecondary,
    backgroundColor: buttonBgPrivateDealSecondary,
    hoverBackgroundColor: buttonHoverBgPrivateDealSecondary,
    activeBackgroundColor: buttonActiveBgPrivateDealSecondary,
    borderColor: buttonBorderPrivateDealSecondary,
    borderWidth: '1px'
  })
}

const btnColorWarning = ({theme}: StyleProps) => {
  const {
    buttonColorWarning,
    buttonHoverColorWarning,
    buttonActiveColorWarning,
    buttonBgWarning,
    buttonHoverBgWarning,
    buttonActiveBgWarning
  } = theme.colors

  return makeStyledButton({
    fontColor: buttonColorWarning,
    hoverFontColor: buttonHoverColorWarning,
    activeFontColor: buttonActiveColorWarning,
    backgroundColor: buttonBgWarning,
    hoverBackgroundColor: buttonHoverBgWarning,
    activeBackgroundColor: buttonActiveBgWarning
  })
}

const btnColorDelete = ({theme}: StyleProps) => {
  const {
    buttonColorDelete,
    buttonHoverColorDelete,
    buttonActiveColorDelete,
    buttonBgDelete,
    buttonHoverBgDelete,
    buttonActiveBgDelete
  } = theme.colors

  return makeStyledButton({
    fontColor: buttonColorDelete,
    hoverFontColor: buttonHoverColorDelete,
    activeFontColor: buttonActiveColorDelete,
    backgroundColor: buttonBgDelete,
    hoverBackgroundColor: buttonHoverBgDelete,
    activeBackgroundColor: buttonActiveBgDelete
  })
}

const btnColorButtonLink = ({theme}: StyleProps) => {
  const {
    buttonColorButtonLink,
    buttonHoverColorButtonLink,
    buttonActiveColorButtonLink,
    buttonBgButtonLink,
    buttonHoverBgButtonLink,
    buttonActiveBgButtonLink
  } = theme.colors

  return makeStyledButton({
    fontColor: buttonColorButtonLink,
    hoverFontColor: buttonHoverColorButtonLink,
    activeFontColor: buttonActiveColorButtonLink,
    backgroundColor: buttonBgButtonLink,
    hoverBackgroundColor: buttonHoverBgButtonLink,
    activeBackgroundColor: buttonActiveBgButtonLink
  })
}

const btnColorButtonWhite = ({theme}: StyleProps) => {
  const {
    buttonColorWhite,
    buttonHoverColorWhite,
    buttonActiveColorWhite,
    buttonBgWhite,
    buttonHoverBgWhite,
    buttonActiveBgWhite
  } = theme.colors
  return makeStyledButton({
    fontColor: buttonColorWhite,
    hoverFontColor: buttonHoverColorWhite,
    activeFontColor: buttonActiveColorWhite,
    backgroundColor: buttonBgWhite,
    hoverBackgroundColor: buttonHoverBgWhite,
    activeBackgroundColor: buttonActiveBgWhite
  })
}

export const withColor = ({appearance, disabledStyle}: StyleProps) => {
  if (appearance === 'noStyle') return null
  if (disabledStyle) return btnColorDisabled

  switch (appearance) {
    case 'secondary':
      return btnColorSecondary
    case 'privateDeal':
      return btnColorPrivateDeal
    case 'privateDealSecondary':
      return btnColorPrivateDealSecondary
    case 'quiet':
      return btnColorQuiet
    case 'warning':
      return btnColorWarning
    case 'delete':
      return btnColorDelete
    case 'link':
      return btnColorLink
    case 'buttonLink':
      return btnColorButtonLink
    case 'white':
      return btnColorButtonWhite
    case 'primary':
    default:
      return btnColorPrimary
  }
}

export const withSize = ({theme, appearance, size}: StyleProps) => {
  const {typography} = theme

  if (appearance === 'noStyle') return null

  if (appearance === 'link')
    return css`
      ${typography.text.action};
    `

  switch (size) {
    case 'lg':
      return css`
        ${typography.text.action};
        padding: 1.2rem 2.8rem;
      `
    case 'xl':
      return css`
        ${typography.text.action};
        padding: ${theme.layout.spacing.s500}px 2.8rem;
      `
    case 'md':
    default:
      return css`
        ${typography.text.action};
        padding: 0.7rem 1.4rem;
      `
  }
}

export const withFullWidth = ({fullWidth}: StyleProps) => {
  if (fullWidth) {
    return css`
      width: 100%;
    `
  }
}

const withLoading = (
  appearance: AppearanceType,
  theme: ThemeType,
  disabledStyle?: boolean
) => {
  let bulletColor
  switch (appearance) {
    case 'primary':
    case 'privateDeal':
      bulletColor = theme.colors.buttonLoadingBulletColorPrimary
      break
    case 'warning':
      bulletColor = theme.colors.buttonLoadingBulletColorWarning
      break
    case 'delete':
      bulletColor = theme.colors.buttonLoadingBulletColorDelete
      break
    case 'secondary':
    default:
      bulletColor = theme.colors.buttonLoadingBulletColorSecondary
  }

  if (disabledStyle) bulletColor = theme.colors.buttonLoadingBulletColorDisabled

  return (
    <ButtonLoadingWrapper>
      <LoadingBullet color={bulletColor} delay="0s" />
      <LoadingBullet color={bulletColor} delay="0.2s" />
      <LoadingBullet color={bulletColor} delay="0.4s" />
    </ButtonLoadingWrapper>
  )
}

const ButtonElement = styled('button')(base, withSize, withFullWidth, withColor)

const Button = forwardRef(
  (props: PropsType, ref: React.MutableRefObject<any>) => {
    const {appearance, disabled, loading, children, dataId} = props
    const theme = useTheme()

    const disabledState = disabled || loading
    const disabledStyle = disabled

    return (
      <ButtonElement
        {...props}
        ref={ref}
        appearance={appearance}
        disabled={disabledState}
        disabledStyle={disabledStyle}
        theme={theme}
        data-id={dataId}
      >
        {loading ? withLoading(appearance, theme, disabledStyle) : children}
      </ButtonElement>
    )
  }
)

Button.defaultProps = {
  alignText: 'center',
  appearance: 'primary',
  type: 'button'
}

export default Button
